import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { useAltHotkeys } from '../../hooks/useAltHotkeys';
import { useModalDrag } from '../../hooks/useModalDrag';
import ModalHeader from '../ui/ModalHeader';
import trackingService, {
  type TraceResult,
  type TraceStatus,
  type TraceStatusRow,
} from '../../services/tracking.service';
import TraceSnapshotPanel, { fmtDate } from './TraceSnapshotPanel';
import LegacyTracePanel, { hazClass } from './LegacyTracePanel';
import { exportSummary, exportTraceData } from './exportTraceData';
import ExportTracePanel from './ExportTracePanel';
import ProDetailSlideOver from '../pro/ProDetailSlideOver';

/**
 * Shared results panel for click-trace (one result) and bulk Trace All (many).
 * Each row shows PRO + status chip + summary; gwiTrace rows with a snapshot get
 * a disclosure with the full shipment detail (open by default when single).
 *
 * Legacy rows come back "queued" (cmsapi forked trace.pl; no webhook push). An
 * opt-in "Watch progress" toggle polls /tracking/trace-status and fills each
 * legacy row's CMS trace fields in as trace.pl completes. Closing the modal (or
 * toggling off) stops the poll; the background trace keeps updating CMS.
 */

interface Props {
  open: boolean;
  title: string;
  results: TraceResult[];
  onClose: () => void;
}

const STATUS_STYLE: Record<TraceStatus, string> = {
  updated: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200',
  queued: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200',
  not_wired: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  not_traceable: 'bg-slate-100 text-slate-700 dark:bg-gray-700 dark:text-gray-200',
  not_available: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  error: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
  stored: 'bg-slate-100 text-slate-700 dark:bg-gray-700 dark:text-gray-200',
  not_found: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  registered: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200',
  wrong_terminal: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
};

const STATUS_LABEL: Record<TraceStatus, string> = {
  updated: 'Updated',
  queued: 'Queued',
  not_wired: 'Legacy',
  not_traceable: 'No tracing',
  not_available: 'Not Available',
  error: 'Error',
  // The popup opened on the last sync while the port is being asked; the
  // real result replaces this row in place a few seconds later.
  stored: 'Last sync',
  // The port answered and has no such booking; the row's line is the badge
  // text. Was the generic "Queued — showing latest snapshot" with the only
  // honest fact folded under Shipment detail (58672276, 2026-10-14).
  not_found: 'Not found',
  // This click registered the booking and queued its first sweep.
  registered: 'Registered',
  // Traced as the other Houston terminal; the row says only where POH has it.
  wrong_terminal: 'Wrong terminal',
};

const POLL_MS = 5000;
const POLL_MAX = 120; // ~10 minutes (a full Trace All of ~500 PROs runs a while)

// Legacy (cmsapi/trace.pl) single-trace result fields: [label, key, isDatetime].
const TRACE_FIELD_ROWS: [string, keyof TraceStatusRow, boolean][] = [
  ['Carrier', 'traceRunCarrier', false],
  ['Event', 'traceEvent', false],
  ['Location', 'traceLocation', false],
  ['Event Date', 'traceEventDatetime', true],
  ['Trace Date', 'traceRunDatetime', true],
  ['Terminate', 'traceTerminateDatetime', true],
];

const legacyStatusLine = (s: TraceStatusRow | undefined): string => {
  if (!s) return 'waiting for results…';
  const parts = [
    s.traceEvent,
    s.traceLocation,
    s.traceEventDatetime ? fmtDate(s.traceEventDatetime) : '',
    s.traceRunCarrier ? `via ${s.traceRunCarrier}` : '',
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : 'traced — no event yet';
};

const TraceResultModal: React.FC<Props> = ({ open, title, results, onClose }) => {
  const [watching, setWatching] = useState(false);
  const [statuses, setStatuses] = useState<Record<number, TraceStatusRow>>({});
  // PRO clicked in the results list — opens the shared detail drawer over the
  // modal. The drawer portals to document.body at z-[100]; while it's open we
  // drop this modal below it (see the outer z-index) so the drawer sits on top.
  const [drawerPro, setDrawerPro] = useState<number | null>(null);
  // PROs already opened this session, so the list shows at a glance which rows
  // have been reviewed (link turns green). Reset on a new result set below.
  const [visited, setVisited] = useState<Set<number>>(() => new Set());
  // Active summary-chip filter (e.g. 'Done', 'No update', 'Queued'); null = show
  // all. Clicking a count in the summary line narrows the list to those rows.
  const [filter, setFilter] = useState<string | null>(null);
  // The batch has "settled" — every legacy row either completed or has gone
  // quiet long enough that it won't (traced with no new data / not traceable).
  const [settled, setSettled] = useState(false);

  const legacyQueued = useMemo(
    () =>
      results
        .filter((r) => r.mode === 'legacy' && r.status === 'queued')
        .map((r) => r.pronumber),
    [results],
  );

  // Reset statuses on a new result set, and auto-start watching legacy queued
  // rows so results populate on open (no manual "Watch progress" click).
  useEffect(() => {
    setStatuses({});
    setSettled(false);
    setVisited(new Set());
    setFilter(null);
    const hasLegacyQueued = results.some(
      (r) => r.mode === 'legacy' && r.status === 'queued',
    );
    setWatching(open && hasLegacyQueued);
  }, [results, open]);

  // Poll CMS trace fields while watching (legacy has no push). Marks the batch
  // "settled" once every row completed, or no new completion has landed for
  // SETTLE_POLLS in a row (the fork finished; the rest won't update), or POLL_MAX.
  useEffect(() => {
    if (!open || !watching || legacyQueued.length === 0) return;
    const baseline = new Map<number, string | null | undefined>();
    results.forEach((r) => {
      if (r.mode === 'legacy' && r.status === 'queued') {
        baseline.set(r.pronumber, r.prev_trace_dt);
      }
    });
    const doneCountOf = (s: Record<number, TraceStatusRow>) =>
      legacyQueued.filter((p) => {
        const b = baseline.get(p);
        if (b === undefined) return false;
        const now = s[p]?.traceRunDatetime ?? null;
        return now != null && now !== b;
      }).length;
    const SETTLE_POLLS = 9; // ~45s of no new completions → the rest won't update
    let cancelled = false;
    let n = 0;
    let prevDone = 0;
    let stale = 0;
    let finished = false;
    const poll = async () => {
      try {
        const s = await trackingService.traceStatus(legacyQueued);
        if (cancelled) return;
        setStatuses(s);
        const dc = doneCountOf(s);
        if (dc > prevDone) {
          prevDone = dc;
          stale = 0;
        } else {
          stale += 1;
        }
        // Settle when all completed, or when completions have landed and then
        // gone quiet. If NOTHING has completed yet, keep polling — the fork is
        // still running (terminal/rail traces are slow); don't prematurely mark
        // the whole batch "No update". POLL_MAX is the hard backstop below.
        if (dc >= legacyQueued.length || (dc > 0 && stale >= SETTLE_POLLS)) {
          finished = true;
          setSettled(true);
        }
      } catch {
        /* transient; keep polling */
      }
    };
    poll();
    const id = setInterval(() => {
      n += 1;
      if (finished || n > POLL_MAX) {
        if (n > POLL_MAX) setSettled(true);
        clearInterval(id);
        return;
      }
      poll();
    }, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [open, watching, legacyQueued, results]);

  // Read-only trace results: Alt+C closes; the layer keeps Alt+S off the grid.
  useAltHotkeys({ C: onClose }, undefined, { layer: open });

  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(open);

  if (!open) return null;

  const single = results.length === 1;
  const counts = results.reduce<Record<string, number>>((acc, r) => {
    acc[r.status] = (acc[r.status] || 0) + 1;
    return acc;
  }, {});

  // A legacy queued row is "done" once the poll reports a Trace Date different
  // from the one captured before the fork fired — trace.pl stamped a fresh run.
  const isDone = (r: TraceResult): boolean => {
    if (r.mode !== 'legacy' || r.status !== 'queued') return false;
    // No baseline captured (older backend) → we can't tell a fresh run from a
    // pre-existing Trace Date, so never flip (avoids false "Done" on load).
    if (r.prev_trace_dt === undefined) return false;
    const now = statuses[r.pronumber]?.traceRunDatetime ?? null;
    return now != null && now !== r.prev_trace_dt;
  };
  const doneCount = results.reduce((n, r) => (isDone(r) ? n + 1 : n), 0);
  // Once settled, a still-queued legacy row that never completed = "No update"
  // (traced with no new data, or not traceable on the chosen side).
  // A gwiTrace EXPORT result that wrote nothing is "queued" to the router
  // (the import resync re-arms the poll job and data lands later by webhook),
  // but a booking placeholder has no poll job and the booking sweep runs on
  // its own cadence, so nothing was queued: it traced and found nothing to
  // update. Show it the way the LYNX popup did.
  const isNoUpdate = (r: TraceResult): boolean =>
    (settled && r.mode === 'legacy' && r.status === 'queued' && !isDone(r)) ||
    (r.mode === 'gwitrace' && r.status === 'queued' && exportTraceData(r) !== null);
  const noUpdateCount = results.reduce((n, r) => (isNoUpdate(r) ? n + 1 : n), 0);

  // Roll completed + settled-no-update rows out of the "Queued" tally.
  const displayCounts = { ...counts };
  displayCounts.queued = Math.max(
    0,
    (displayCounts.queued || 0) - doneCount - noUpdateCount,
  );
  const summarySegments: { label: string; count: number }[] = Object.entries(displayCounts)
    .filter(([, v]) => v > 0)
    .map(([k, v]) => ({ label: STATUS_LABEL[k as TraceStatus] ?? k, count: v }));
  if (noUpdateCount > 0) summarySegments.unshift({ label: 'No update', count: noUpdateCount });
  if (doneCount > 0) summarySegments.unshift({ label: 'Done', count: doneCount });

  // The summary label a row falls under — mirrors how the counts above are
  // bucketed, so a chip filter matches exactly what its count tallied.
  const rowLabel = (r: TraceResult): string => {
    if (isDone(r)) return 'Done';
    if (isNoUpdate(r)) return 'No update';
    return STATUS_LABEL[r.status] ?? r.status;
  };
  // Ignore a filter whose bucket has emptied out (e.g. "Queued" rows all settled
  // into Done/No update) so the list never goes blank under a stale selection.
  const activeFilter =
    filter && summarySegments.some((s) => s.label === filter) ? filter : null;
  const visibleResults = activeFilter
    ? results.filter((r) => rowLabel(r) === activeFilter)
    : results;

  return createPortal(
    <>
    <div
      className={`fixed inset-0 flex items-center justify-center bg-slate-900/40 p-6 ${
        drawerPro !== null ? 'z-[90]' : 'z-[120]'
      }`}
      role="dialog"
      aria-modal="true"
      onClick={() => {
        // Don't let a backdrop click close the modal out from under an open
        // drawer — the drawer sits on top and owns the foreground.
        if (drawerPro === null) onClose();
      }}
    >
      <div
        ref={panelRef}
        style={panelStyle}
        className="flex max-h-[85vh] w-full max-w-[760px] flex-col rounded-lg border border-slate-200 bg-panel shadow-xl dark:border-gray-700"
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeader title={title} onClose={onClose} dragHandleProps={dragHandleProps} />

        {!single && (
          <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-2 text-[0.75rem] text-slate-600 dark:border-gray-700 dark:text-gray-300">
            <span className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
              <button
                type="button"
                onClick={() => setFilter(null)}
                title="Show all"
                className={`rounded px-1 hover:bg-slate-100 dark:hover:bg-gray-700 ${
                  activeFilter === null ? 'font-semibold text-slate-900 dark:text-gray-100' : ''
                }`}
              >
                {results.length} PRO{results.length === 1 ? '' : 's'}
              </button>
              {summarySegments.map((seg) => (
                <React.Fragment key={seg.label}>
                  <span className="text-slate-300 dark:text-gray-600">·</span>
                  <button
                    type="button"
                    onClick={() =>
                      setFilter((f) => (f === seg.label ? null : seg.label))
                    }
                    title={`Show only ${seg.label}`}
                    className={`rounded px-1 hover:bg-slate-100 dark:hover:bg-gray-700 ${
                      activeFilter === seg.label
                        ? 'font-semibold text-slate-900 underline dark:text-gray-100'
                        : ''
                    }`}
                  >
                    {seg.count} {seg.label}
                  </button>
                </React.Fragment>
              ))}
            </span>
            {legacyQueued.length > 0 && watching && !settled && (
              <span className="shrink-0 text-[0.6875rem] text-slate-400 dark:text-gray-500">
                watching…
              </span>
            )}
          </div>
        )}

        {single && results[0]?.snapshot && results[0].status !== 'wrong_terminal' && (
          <div className="border-b border-slate-200 px-4 py-2 text-[0.75rem] text-slate-500 dark:border-gray-700 dark:text-gray-400">
            Shipment ID: {results[0].shipment_id ?? '—'}
            {results[0].last_synced_at ? `  |  Last Synced: ${fmtDate(results[0].last_synced_at)}` : ''}
          </div>
        )}

        <div className="flex-1 overflow-auto px-4 py-3">
          {visibleResults.map((r) => {
            // Traced as the other Houston terminal: the row is the one line
            // saying where POH has it, and NO data — the cue LYNX gave by
            // returning nothing. The snapshot is still on the result; this is
            // where it is ignored.
            const wrongTerminal = r.status === 'wrong_terminal';
            // A gwiTrace EXPORT trace renders through the legacy popup layout
            // (the LYNX one CSRs know) via the exportTraceData adapter, with
            // the PRO / status / "Traced on" header row the legacy rows get.
            const exportData = r.mode === 'gwitrace' && !wrongTerminal ? exportTraceData(r) : null;
            const panelOnly = single && !!r.snapshot && !exportData && !wrongTerminal;
            const isLegacyQueued = r.mode === 'legacy' && r.status === 'queued';
            const done = isDone(r);
            const noUpdate = isNoUpdate(r);
            const haz = hazClass(r.trace_data);
            return (
              <div
                key={r.pronumber}
                className="mb-2 border-b border-slate-100 pb-2 last:mb-0 last:border-0 dark:border-gray-700/50"
              >
                {!panelOnly && (
                  <>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setDrawerPro(r.pronumber);
                          setVisited((prev) => new Set(prev).add(r.pronumber));
                        }}
                        className={`rounded-md border-2 px-1.5 py-0.5 text-[0.75rem] hover:underline ${
                          visited.has(r.pronumber)
                            ? 'border-green-500 bg-green-50 text-purple-700 dark:bg-green-900/30 dark:text-purple-300'
                            : 'border-transparent text-blue-600 dark:text-blue-400'
                        }`}
                        title="Open PRO details"
                      >
                        PRO {r.pronumber}
                      </button>
                      <a
                        href={`/searchresults?q=${r.pronumber}&pro=${r.pronumber}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-slate-400 hover:text-blue-600 dark:text-gray-500 dark:hover:text-blue-400"
                        title="Open PRO in new tab"
                        aria-label={`Open PRO ${r.pronumber} in a new tab`}
                      >
                        <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                      </a>
                      {r.container && (
                        <span className="text-[0.6875rem] text-slate-500 dark:text-gray-400">
                          {r.container}
                        </span>
                      )}
                      {haz && (
                        <span
                          className="rounded bg-red-100 px-1.5 py-0.5 text-[0.625rem] font-bold text-red-800 dark:bg-red-900/40 dark:text-red-200"
                          title={`Terminal reports this container as hazardous — class ${haz}`}
                        >
                          HAZ {haz}
                        </span>
                      )}
                      <span
                        className={`rounded px-1.5 py-0.5 text-[0.625rem] font-semibold uppercase ${
                          done
                            ? STATUS_STYLE.updated
                            : noUpdate
                              ? STATUS_STYLE.not_traceable
                              : STATUS_STYLE[r.status]
                        }`}
                      >
                        {done ? 'Done' : noUpdate ? 'No update' : STATUS_LABEL[r.status]}
                      </span>
                    </div>
                    {isLegacyQueued ? (
                      // Legacy rows: show what the trace produced once it's Done;
                      // while still queued, don't show the initial/stale status.
                      done ? (
                        <div className="mt-0.5 text-[0.75rem] text-slate-700 dark:text-gray-200">
                          {legacyStatusLine(statuses[r.pronumber])}
                        </div>
                      ) : null
                    ) : wrongTerminal ? (
                      <div className="mt-1 text-[0.8125rem] font-medium text-amber-800 dark:text-amber-200">
                        {r.summary}
                      </div>
                    ) : (
                      <div className="mt-0.5 text-[0.75rem] text-slate-600 dark:text-gray-300">
                        {exportData ? exportSummary(r) : r.summary}
                      </div>
                    )}
                    {r.trace_data ? (
                      <div className="mt-2">
                        <LegacyTracePanel data={r.trace_data} />
                      </div>
                    ) : exportData ? (
                      <div className="mt-2">
                        <ExportTracePanel result={r} data={exportData} />
                      </div>
                    ) : r.trace_fields ? (
                      <div className="mt-1 rounded border border-slate-200 bg-slate-50 p-2 dark:border-gray-700 dark:bg-gray-900">
                        {TRACE_FIELD_ROWS.map(([label, key, isDate]) => {
                          const raw = r.trace_fields![key];
                          const val = isDate ? fmtDate(raw) : raw || '';
                          if (!val) return null;
                          return (
                            <div key={key} className="flex gap-2 py-0.5 text-[0.75rem]">
                              <span className="w-24 shrink-0 text-right font-medium text-slate-500 dark:text-gray-400">
                                {label}
                              </span>
                              <span className="break-words text-slate-900 dark:text-gray-100">{val}</span>
                            </div>
                          );
                        })}
                      </div>
                    ) : null}
                  </>
                )}
                {r.snapshot &&
                  !exportData &&
                  !wrongTerminal &&
                  (single ? (
                    <div className={panelOnly ? '' : 'mt-2'}>
                      <TraceSnapshotPanel result={r} showHeader={false} />
                    </div>
                  ) : (
                    <details className="mt-1">
                      <summary className="cursor-pointer text-[0.6875rem] text-blue-600 dark:text-blue-400">
                        Shipment detail
                      </summary>
                      <div className="mt-1">
                        <TraceSnapshotPanel result={r} />
                      </div>
                    </details>
                  ))}
              </div>
            );
          })}
        </div>

        <div className="flex justify-end border-t border-slate-200 px-4 py-3 dark:border-gray-700">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-blue-600 bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
          >
            Close
          </button>
        </div>
      </div>
    </div>
    <ProDetailSlideOver
      pronumber={drawerPro}
      isOpen={drawerPro !== null}
      onClose={() => setDrawerPro(null)}
    />
    </>,
    document.body,
  );
};

export default TraceResultModal;
